/**
 * DraggableMentalityFab — iOS AssistiveTouch-style floating button.
 *
 * Hold-and-drag to reposition; release snaps to the nearest viewport
 * edge. Short taps (<6px drift) still open the overlay. Position
 * persists across reloads via localStorage.
 *
 * Desktop vs mobile:
 *   - Desktop: only left/right edges are valid snap targets (top would
 *     hide behind the TopBar; bottom would overlap composer Send).
 *     Left edge is anchored to `.layout-main` inset so the sidebar
 *     column doesn't render over the FAB.
 *   - Mobile: all four edges are valid; no sidebar inset to worry
 *     about because the drawer overlays content.
 */

import { useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';

type FabPosition = { edge: 'left' | 'right' | 'top' | 'bottom'; ratio: number };
const FAB_POSITION_KEY = 'mentality_fab_pos_v1';
const FAB_MARGIN = 12;
const FAB_SIZE = 44;
const DRAG_THRESHOLD = 6;
const DESKTOP_BREAKPOINT = 768;
const TOPBAR_CLEARANCE = 80;
const COMPOSER_CLEARANCE = 120;

function isDesktopViewport(): boolean {
    return typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT;
}

function mainContentLeftInset(): number {
    if (typeof document === 'undefined') return 0;
    const main = document.querySelector<HTMLElement>('.layout-main');
    if (!main) return 0;
    const rect = main.getBoundingClientRect();
    return Math.max(0, rect.left);
}

function loadFabPosition(): FabPosition {
    try {
        const raw = localStorage.getItem(FAB_POSITION_KEY);
        if (!raw) return { edge: 'right', ratio: 0.85 };
        const parsed = JSON.parse(raw) as Partial<FabPosition>;
        if (
            (parsed.edge === 'left' || parsed.edge === 'right'
                || parsed.edge === 'top' || parsed.edge === 'bottom')
            && typeof parsed.ratio === 'number'
            && parsed.ratio >= 0 && parsed.ratio <= 1
        ) {
            return parsed as FabPosition;
        }
    } catch { /* ignore */ }
    return { edge: 'right', ratio: 0.85 };
}

function saveFabPosition(pos: FabPosition) {
    try { localStorage.setItem(FAB_POSITION_KEY, JSON.stringify(pos)); } catch { /* quota */ }
}

function clamp(n: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, n));
}

function anchorToXY(pos: FabPosition): { left: number; top: number } {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const usableX = Math.max(0, vw - FAB_SIZE);
    const usableY = Math.max(0, vh - FAB_SIZE);
    const desktop = isDesktopViewport();
    const leftEdge = desktop ? mainContentLeftInset() + FAB_MARGIN : FAB_MARGIN;
    const minTop = desktop ? TOPBAR_CLEARANCE : FAB_MARGIN;
    const maxTop = desktop ? Math.max(minTop, vh - FAB_SIZE - COMPOSER_CLEARANCE) : usableY - FAB_MARGIN;
    const yFromRatio = clamp(pos.ratio * vh - FAB_SIZE / 2, minTop, maxTop);
    switch (pos.edge) {
        case 'left':
            return { left: leftEdge, top: yFromRatio };
        case 'right':
            return { left: usableX - FAB_MARGIN, top: yFromRatio };
        case 'top':
            return { left: clamp(pos.ratio * vw - FAB_SIZE / 2, leftEdge, usableX - FAB_MARGIN), top: minTop };
        case 'bottom':
            return { left: clamp(pos.ratio * vw - FAB_SIZE / 2, leftEdge, usableX - FAB_MARGIN), top: maxTop };
    }
}

function snapToNearestEdge(left: number, top: number): FabPosition {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cx = left + FAB_SIZE / 2;
    const cy = top + FAB_SIZE / 2;
    const distances: Record<FabPosition['edge'], number> = {
        left: cx,
        right: vw - cx,
        top: cy,
        bottom: vh - cy,
    };
    const edges: FabPosition['edge'][] = isDesktopViewport()
        ? ['left', 'right']
        : ['left', 'right', 'top', 'bottom'];
    const best = edges.reduce<FabPosition['edge']>(
        (acc, k) => (distances[k] < distances[acc] ? k : acc),
        'right',
    );
    const ratio = best === 'left' || best === 'right' ? cy / vh : cx / vw;
    return { edge: best, ratio: clamp(ratio, 0.02, 0.98) };
}

export default function DraggableMentalityFab({ onOpen }: { onOpen: () => void }) {
    const [pos, setPos] = useState<FabPosition>(() => loadFabPosition());
    const [coords, setCoords] = useState<{ left: number; top: number }>(() => anchorToXY(loadFabPosition()));
    const [dragging, setDragging] = useState(false);
    const movedRef = useRef(false);
    const startRef = useRef<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null);

    useEffect(() => {
        const onResize = () => {
            if (isDesktopViewport() && (pos.edge === 'top' || pos.edge === 'bottom')) {
                const coerced: FabPosition = { edge: 'right', ratio: 0.7 };
                setPos(coerced);
                saveFabPosition(coerced);
                setCoords(anchorToXY(coerced));
                return;
            }
            setCoords(anchorToXY(pos));
        };
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onResize);
        };
    }, [pos]);

    const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
        if (e.button !== 0) return;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        movedRef.current = false;
        startRef.current = {
            x: e.clientX,
            y: e.clientY,
            offsetX: e.clientX - coords.left,
            offsetY: e.clientY - coords.top,
        };
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
        if (!startRef.current) return;
        const dx = e.clientX - startRef.current.x;
        const dy = e.clientY - startRef.current.y;
        if (!movedRef.current && Math.hypot(dx, dy) >= DRAG_THRESHOLD) {
            movedRef.current = true;
            setDragging(true);
        }
        if (!movedRef.current) return;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const desktop = isDesktopViewport();
        const minLeft = desktop ? mainContentLeftInset() : 0;
        const minTop = desktop ? TOPBAR_CLEARANCE : 0;
        const maxTop = desktop
            ? Math.max(minTop, vh - FAB_SIZE - COMPOSER_CLEARANCE)
            : vh - FAB_SIZE;
        const left = clamp(e.clientX - startRef.current.offsetX, minLeft, vw - FAB_SIZE);
        const top = clamp(e.clientY - startRef.current.offsetY, minTop, maxTop);
        setCoords({ left, top });
    };

    const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
        (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
        if (!startRef.current) return;

        if (!movedRef.current) {
            startRef.current = null;
            setDragging(false);
            onOpen();
            return;
        }

        const snapped = snapToNearestEdge(coords.left, coords.top);
        setPos(snapped);
        saveFabPosition(snapped);
        setCoords(anchorToXY(snapped));
        setDragging(false);
        startRef.current = null;
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen();
        }
    };

    const fabStyle = {
        '--fab-left': `${coords.left}px`,
        '--fab-top': `${coords.top}px`,
    } as React.CSSProperties;

    return (
        <button
            type="button"
            className={`mentality-overlay__fab mentality-overlay__fab--draggable${dragging ? ' is-dragging' : ''}`}
            style={fabStyle}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onKeyDown={handleKeyDown}
            aria-label="Open Mentality (hold and drag to move)"
            title="Mentality"
        >
            <Sparkles size={18} aria-hidden="true" />
            <span className="mentality-overlay__fab-label">Mentality</span>
        </button>
    );
}
